import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
  useSyncExternalStore,
} from "react";
import { Settings, STAGE_DIMENSIONS } from "../settings";
import { PixiSettings, isMobileDevice } from "../pixiSettings";
import { pauseIosFullscreenState, isIOS, isSafari } from "../utilities";
import { FEATURES_CONFIG } from "../featuresConfig";

// Static grid reference dimensions for UI scaling (never changes per game)
const GRID_SYMBOL_WIDTH = 200;
const GRID_SYMBOL_HEIGHT = 200;

// Responsive breakpoints for layout calculations
export const ASPECT_RATIO_BREAKPOINTS = {
  PHONE: 540,
  TABLET: 1024,
  DESKTOP_MIN_WIDTH: 1367,
  DESKTOP_ASPECT_RATIO: 1.56,
  SMALL_DESKTOP_MAX_WIDTH: 1919,
  WIDE_DESKTOP_MIN_WIDTH: 2540,
};

// Ultra-wide: above this width, allow symbols to scale past 1.0 (up to MAX_SCALE)
export const ULTRA_WIDE = {
  BREAKPOINT: 2800,
  MAX_SCALE: 2.0,
}

// Layout configurations for different device orientations and types
const LAYOUT_CONFIGS = {
  MOBILE_SMALL_PORTRAIT: {
    paddingSides: 0,
    paddingTop: GRID_SYMBOL_HEIGHT * 4,
    paddingBottom: GRID_SYMBOL_HEIGHT * 3,
  },
  MOBILE_SMALL_LANDSCAPE: {
    paddingSides: GRID_SYMBOL_WIDTH * 4.5,
    paddingTop: GRID_SYMBOL_HEIGHT * 1.05,
    paddingBottom: GRID_SYMBOL_HEIGHT * 1.05,
  },
  MOBILE_PORTRAIT: {
    paddingSides: 0,
    paddingTop: GRID_SYMBOL_HEIGHT * 4,
    paddingBottom: GRID_SYMBOL_HEIGHT * 3,
  },
  MOBILE_LANDSCAPE: {
    paddingSides: GRID_SYMBOL_WIDTH * 4.5,
    paddingTop: GRID_SYMBOL_HEIGHT * 1.05,
    paddingBottom: GRID_SYMBOL_HEIGHT * 1.05,
  },
  // TABLET_PORTRAIT: {
  //   paddingSides: GRID_SYMBOL_WIDTH * 4.5,
  //   paddingTop: GRID_SYMBOL_HEIGHT * 6,
  //   paddingBottom: GRID_SYMBOL_HEIGHT,
  // },
  // An iPad upright (2026-10-29): MOBILE_PORTRAIT's, more top / bottom — the
  // buttons and texts ~10% smaller, as the platform's own games draw them.
  TABLET_PORTRAIT: {
    paddingSides: 0,
    paddingTop: GRID_SYMBOL_HEIGHT * 4.6,
    paddingBottom: GRID_SYMBOL_HEIGHT * 3.5,
  },
  // TABLET_LANDSCAPE: {
  //   paddingSides: GRID_SYMBOL_WIDTH * 5,
  //   paddingTop: GRID_SYMBOL_HEIGHT * 2.25,
  //   paddingBottom: GRID_SYMBOL_HEIGHT * 2.25,
  // },
  // MOBILE_LANDSCAPE's — an iPad sideways (2026-10-29): the phone's UI.
  TABLET_LANDSCAPE: {
    paddingSides: GRID_SYMBOL_WIDTH * 4.5,
    paddingTop: GRID_SYMBOL_HEIGHT * 1.05,
    paddingBottom: GRID_SYMBOL_HEIGHT * 1.05,
  },
  SMALL_DESKTOP: {
    paddingSides: GRID_SYMBOL_WIDTH * 4,
    paddingTop: GRID_SYMBOL_HEIGHT,
    paddingBottom: GRID_SYMBOL_HEIGHT,
  },
  DESKTOP: {
    paddingSides: GRID_SYMBOL_WIDTH * 1.95,
    paddingTop: GRID_SYMBOL_HEIGHT,
    paddingBottom: GRID_SYMBOL_HEIGHT,
  },
  // WIDE_DESKTOP: {
  //   paddingSides: GRID_SYMBOL_WIDTH * 1.95,
  //   paddingTop: GRID_SYMBOL_HEIGHT * 0.75,
  //   paddingBottom: GRID_SYMBOL_HEIGHT * 0.75,
  // },
  // Desktop's, as ULTRA_WIDE_DESKTOP (2026-10-23: a 2K window's intro and its
  // TAP TO START came out small — "should look same as on desktop").
  WIDE_DESKTOP: {
    paddingSides: GRID_SYMBOL_WIDTH * 1.95,
    paddingTop: GRID_SYMBOL_HEIGHT,
    paddingBottom: GRID_SYMBOL_HEIGHT,
  },
  // ULTRA_WIDE_DESKTOP: {
  //   paddingSides: GRID_SYMBOL_WIDTH * 1.95,
  //   paddingTop: GRID_SYMBOL_HEIGHT * 0.5,
  //   paddingBottom: GRID_SYMBOL_HEIGHT * 0.5,
  // },
  // Desktop's (2026-10-22): half a symbol made the UI symbol ~25% of the
  // height (desktop ~20%) — the intro, the dialogs' tap line and the buttons
  // all came out of proportion on an ultra-wide window.
  ULTRA_WIDE_DESKTOP: {
    paddingSides: GRID_SYMBOL_WIDTH * 1.95,
    paddingTop: GRID_SYMBOL_HEIGHT,
    paddingBottom: GRID_SYMBOL_HEIGHT,
  },
};

// Layout type constants. Exported because these are the keys of
// PixiSettings.REELSET_LAYOUT_CONFIGS and of any per-layout config a component
// keeps — see GameViewSimpleController's scene offsets. Deriving the layout a
// second time elsewhere would mean two classifiers that drift apart.
export const LAYOUT_TYPES = {
  MOBILE_SMALL_PORTRAIT: "MOBILE_SMALL_PORTRAIT",
  MOBILE_SMALL_LANDSCAPE: "MOBILE_SMALL_LANDSCAPE",
  MOBILE_PORTRAIT: "MOBILE_PORTRAIT",
  MOBILE_LANDSCAPE: "MOBILE_LANDSCAPE",
  TABLET_PORTRAIT: "TABLET_PORTRAIT",
  TABLET_LANDSCAPE: "TABLET_LANDSCAPE",
  SMALL_DESKTOP: "SMALL_DESKTOP",
  DESKTOP: "DESKTOP",
  WIDE_DESKTOP: "WIDE_DESKTOP",
  ULTRA_WIDE_DESKTOP: "ULTRA_WIDE_DESKTOP",
};

// Device detection thresholds
const DEVICE_DETECTION = {
  SURFACE_DUO_ASPECT_RATIO: 2.5,
  SURFACE_DUO_MIN_DIMENSION: 400,
  LARGE_MOBILE_WIDTH: 900,
  LARGE_MOBILE_MIN_DIMENSION: 600,
  SQUARE_DEVICE_TOLERANCE: 0.25,
  LANDSCAPE_ASPECT_RATIO: 1.1,
  // Small mobile threshold: devices with max dimension <= this are MOBILE_SMALL
  // Catches iPhone SE/8/7/6s (667px) and Plus models (736px)
  SMALL_MOBILE_MAX_DIMENSION: 736,
  // ── ...or SHORT for its width, upright (2026-10-30, the demo's iPhone 11) ──
  // Safari's bars left a 414x743 frame: just over 736, so it took the
  // standard layout, tuned for tall phones (~0.46), and the top row and the
  // Hold & Win box overflowed. A phone (short side under PHONE_MAX_SHORT_SIDE)
  // upright at width / height >= this is small too — the small layout is
  // tuned on exactly that shape (375x667, 0.56). Bars hidden, it is tall
  // again and takes the standard layout.
  SHORT_PHONE_MIN_ASPECT: 0.52,
  // Phone vs tablet by the SHORT side: phones are under it, every iPad is
  // 744+ even with Safari's bars (the long side is not safe — an iPad mini's
  // drops under 1000 with them).
  PHONE_MAX_SHORT_SIDE: 600,
};

const DIVIDER_MAX_WIDTH = 4;

// ── The platform's own device test (their dev, 2026-10-30: "kod nas je
// touchscreen ako je 'ontouchstart' in window ili navigator.maxTouchPoints >
// 0") ── a touch screen is one with touch events or touch points. Upright,
// that alone makes the phone / tablet layout. Sideways, a FINE main pointer
// (mouse / trackpad) stays desktop, as their info bar decides it (pointer:
// fine → desktop) — so a touch laptop in a casino's iframe never takes a
// mobile or tablet layout (their CEO, 2026-10-29).
const hasTouchSupport = () =>
  typeof window !== "undefined" &&
  ("ontouchstart" in window || (window.navigator?.maxTouchPoints ?? 0) > 0);
const hasFinePointer = () =>
  typeof window !== "undefined" && !!window.matchMedia?.("(pointer: fine)").matches;
// Wider than tall for the layout (near-square counts as sideways).
const isSidewaysFrame = (aspect) =>
  (Math.abs(aspect - 1.0) <= DEVICE_DETECTION.SQUARE_DEVICE_TOLERANCE && aspect >= 0.85) ||
  aspect > DEVICE_DETECTION.LANDSCAPE_ASPECT_RATIO;
/** DESKTOP by its input, for a frame of `aspect` (width / height). */
const isDesktopByInput = (aspect) =>
  !hasTouchSupport() || (isSidewaysFrame(aspect) && hasFinePointer());


export const DimensionsContext = createContext(null);

// The same value as a store, for useDimensionsWhen: a component that reads the
// size through it is NOT a context consumer, so a resize re-renders it only
// when it asks to follow the size. Module-level, not a context: there is one
// DimensionsProvider, and the PIXI tree only sees contexts Stage bridges.
// On globalThis so a hot reload of this module reuses the live store.
const dimensionsStore =
  globalThis.__dimensionsStore ||
  (globalThis.__dimensionsStore = { value: null, listeners: new Set() });

// Main context provider for responsive layout management
export const DimensionsProvider = ({ children }) => {
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.visualViewport?.width ?? window.innerWidth,
    height: window.visualViewport?.height ?? window.innerHeight,
  });

  // Lock dimensions when keyboard is open to prevent game shrinking
  const [lockedDimensions, setLockedDimensions] = useState(null);
  // Mirror of lockedDimensions readable from the focus listeners, whose effect
  // has [] deps and would otherwise only see the stale initial value.
  const lockedDimensionsRef = useRef(null);
  // Debounce latch for releasing the keyboard lock — rides over the iOS
  // blur→refocus bounce so the lock is only released once focus truly leaves.
  const keyboardUnlockTimeoutRef = useRef(null);
  // Last emitted `dimensions` — lets the memo keep a stable reference when the
  // effective size is unchanged (e.g. keyboard lock/unlock), avoiding a
  // whole-app re-render for a no-op size change.
  const prevDimensionsRef = useRef(null);
  // Post-unlock cooldown: blocks resize events briefly after the keyboard lock
  // is released so Safari's keyboard-close animation doesn't inject transitional
  // dimensions. Only genuine later resizes (rotation, window drag) are processed.
  const resizeCooldownRef = useRef(0);
  // Forced keyboard dismiss on orientation flip — keep the layout lock until
  // visualViewport recovers so we never feed keyboard-shrunk landscape sizes
  // into the game (that briefly looks like tablet). Separate from user-close.
  const rotationDismissRef = useRef(false);

  // Keep the ref in sync so the focus listeners can read the current lock.
  useEffect(() => {
    lockedDimensionsRef.current = lockedDimensions;
  }, [lockedDimensions]);

  // Handle input focus/blur to lock dimensions when keyboard opens.
  // The keyboard shrinks visualViewport.height; without this lock the game
  // re-reads that shrunken height and re-lays-out as a smaller device (the
  // "renders as tablet, pushed to the top half" bug). We freeze dimensions to
  // the pre-keyboard size for as long as an input is focused.
  useEffect(() => {
    const isTextInput = (el) =>
      !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA");

    const handleFocusIn = (e) => {
      if (!isTextInput(e.target)) return;

      // A pending unlock means we're mid iOS blur→refocus bounce — cancel it so
      // the lock is never released between the bounce's blur and refocus.
      if (keyboardUnlockTimeoutRef.current) {
        clearTimeout(keyboardUnlockTimeoutRef.current);
        keyboardUnlockTimeoutRef.current = null;
      }

      // Capture the pre-keyboard size ONCE. iOS fires repeated focus events
      // (the anti-blur refocus burst) while the keyboard is already up and the
      // viewport is shrunk — re-capturing then would lock the game at the
      // shrunken size. `prev ?? …` keeps the first, full-size capture.
      setLockedDimensions((prev) =>
        prev ?? {
          width: window.visualViewport?.width ?? window.innerWidth,
          height: window.visualViewport?.height ?? window.innerHeight,
        },
      );
    };

    const handleFocusOut = (e) => {
      if (!isTextInput(e.target)) return;

      // Rotation forced-dismiss owns unlock (waits for viewport to recover).
      // Don't arm the user-close debounce/cooldown on top of that.
      if (rotationDismissRef.current) return;
      if (!lockedDimensionsRef.current) return;

      // Don't unlock immediately: iOS blurs then refocuses the input several
      // times over ~1s (see HiddenAutoplayInputs' anti-blur). Debounce and only
      // release once focus has truly left every text input, so the game never
      // flickers to the shrunken size mid-bounce.
      if (keyboardUnlockTimeoutRef.current) {
        clearTimeout(keyboardUnlockTimeoutRef.current);
      }
      keyboardUnlockTimeoutRef.current = setTimeout(() => {
        keyboardUnlockTimeoutRef.current = null;
        if (isTextInput(document.activeElement)) return; // refocused — stay locked
        if (rotationDismissRef.current) return;

        // Carry the locked (pre-keyboard) size into windowDimensions. No
        // viewport re-read — the locked size IS the correct full-screen size.
        const locked = lockedDimensionsRef.current;
        if (locked) {
          setWindowDimensions((prev) =>
            prev.width === locked.width && prev.height === locked.height
              ? prev
              : { width: locked.width, height: locked.height },
          );
        }

        // Start a post-unlock cooldown so the resize handler ignores any
        // lingering keyboard-close resize events from Safari before
        // processing genuine resizes again.
        resizeCooldownRef.current = Date.now();
        setLockedDimensions(null);
      }, 800);
    };

    document.addEventListener("focusin", handleFocusIn, true);
    document.addEventListener("focusout", handleFocusOut, true);

    return () => {
      document.removeEventListener("focusin", handleFocusIn, true);
      document.removeEventListener("focusout", handleFocusOut, true);
      if (keyboardUnlockTimeoutRef.current) {
        clearTimeout(keyboardUnlockTimeoutRef.current);
        keyboardUnlockTimeoutRef.current = null;
      }
    };
  }, []);

  // Forced keyboard dismiss on orientation flip only (portrait ↔ landscape).
  // Never feed visualViewport while the keyboard is still up — that shrunken
  // landscape size makes the game briefly look like a tablet. Instead: blur,
  // re-lock to the full layout size (innerWidth/innerHeight), and hold until
  // visualViewport recovers. User-close path is untouched.
  useEffect(() => {
    const isLandscapeNow = () => {
      // Prefer OS orientation — visualViewport aspect lies while the keyboard
      // is open (shrunken height can look portrait even after a rotate).
      const type = window.screen?.orientation?.type;
      if (type) return type.startsWith("landscape");
      if (typeof window.orientation === "number") {
        return Math.abs(window.orientation) === 90;
      }
      return window.innerWidth > window.innerHeight;
    };

    // Layout viewport — does NOT shrink with the soft keyboard on iOS.
    // visualViewport does; never use it for the rotation re-lock.
    const getFullLayoutSize = (nowLandscape) => {
      let width = window.innerWidth;
      let height = window.innerHeight;
      const aspectOk = nowLandscape ? width > height : width <= height;
      if (!aspectOk && window.screen?.width && window.screen?.height) {
        const longSide = Math.max(window.screen.width, window.screen.height);
        const shortSide = Math.min(window.screen.width, window.screen.height);
        width = nowLandscape ? longSide : shortSide;
        height = nowLandscape ? shortSide : longSide;
      }
      return { width, height };
    };

    const closeKeyboardOnRotation = () => {
      const locked = lockedDimensionsRef.current;
      if (!locked) return; // keyboard closed — nothing to do

      const wasLandscape = locked.width > locked.height;
      const nowLandscape = isLandscapeNow();
      if (nowLandscape === wasLandscape) return; // not a rotation

      const next = getFullLayoutSize(nowLandscape);

      // Own unlock; cancel any user-close debounce so it can't drop the lock
      // onto a keyboard-shrunk visualViewport mid-animation.
      if (keyboardUnlockTimeoutRef.current) {
        clearTimeout(keyboardUnlockTimeoutRef.current);
        keyboardUnlockTimeoutRef.current = null;
      }
      rotationDismissRef.current = true;
      resizeCooldownRef.current = 0;
      lockedDimensionsRef.current = next;
      setLockedDimensions(next);
      setWindowDimensions(next);

      // Turn off HiddenAutoplayInputs' anti-blur guard, else it refocuses.
      const refs = window.autoplayInputRefs;
      if (refs?.shouldMaintainFocusRef) {
        refs.shouldMaintainFocusRef.current = false;
      }
      const active = document.activeElement;
      if (
        active &&
        (active.tagName === "INPUT" || active.tagName === "TEXTAREA")
      ) {
        active.blur();
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", closeKeyboardOnRotation);
    }
    window.addEventListener("resize", closeKeyboardOnRotation);
    window.addEventListener("orientationchange", closeKeyboardOnRotation);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          closeKeyboardOnRotation,
        );
      }
      window.removeEventListener("resize", closeKeyboardOnRotation);
      window.removeEventListener("orientationchange", closeKeyboardOnRotation);
    };
  }, []);

  // After iOS Safari fullscreen engages while the keyboard lock is held: adopt
  // any GROWTH in layout size (toolbar gone), never shrink toward the keyboard
  // visible area. Fired from IosFullscreenOverlayController.onFullscreenAchieved.
  useEffect(() => {
    const handleIosFullscreenAchieved = () => {
      const locked = lockedDimensionsRef.current;
      if (!locked) return;

      const w = window.innerWidth;
      const h = window.innerHeight;
      const next = {
        width: Math.max(locked.width, w),
        height: Math.max(locked.height, h),
      };

      if (next.width === locked.width && next.height === locked.height) {
        return;
      }

      setLockedDimensions(next);
      setWindowDimensions((prev) =>
        prev.width === next.width && prev.height === next.height ? prev : next,
      );
    };

    window.addEventListener("ios-fullscreen-achieved", handleIosFullscreenAchieved);
    return () => {
      window.removeEventListener(
        "ios-fullscreen-achieved",
        handleIosFullscreenAchieved,
      );
    };
  }, []);

  // Handle window resize — subscribed once on mount.
  // Uses refs (lockedDimensionsRef, resizeCooldownRef) instead of state so the
  // effect never re-runs on lock/unlock. This prevents the initial handleResize()
  // call from re-reading a transitional viewport size when the keyboard lock is
  // released. Only genuine resize events (rotation, window drag) update dimensions.
  useEffect(() => {
    const handleResize = () => {
      // Dev only: the resize profiler (utils/resizeProfiler) registers this
      // when DEV_TOOLS.RESIZE_PROFILER is on — no import, so nothing of it
      // loads with this context.
      globalThis.__markResize?.();

      // Rotation forced-dismiss: hold the full layout lock until the keyboard
      // is gone. Never adopt visualViewport while it's still keyboard-shrunk.
      if (rotationDismissRef.current && lockedDimensionsRef.current) {
        const vvW = window.visualViewport?.width ?? window.innerWidth;
        const vvH = window.visualViewport?.height ?? window.innerHeight;
        const layoutW = window.innerWidth;
        const layoutH = window.innerHeight;
        const locked = lockedDimensionsRef.current;

        // Grow the lock if the layout viewport grew (toolbar hide / settle).
        const grown = {
          width: Math.max(locked.width, layoutW),
          height: Math.max(locked.height, layoutH),
        };
        if (grown.width !== locked.width || grown.height !== locked.height) {
          lockedDimensionsRef.current = grown;
          setLockedDimensions(grown);
          setWindowDimensions(grown);
        }

        // Keyboard gone when visualViewport roughly matches layout viewport.
        const keyboardGone =
          vvH >= layoutH * 0.9 && vvW >= layoutW * 0.9;
        if (keyboardGone) {
          rotationDismissRef.current = false;
          lockedDimensionsRef.current = null;
          setLockedDimensions(null);
          setWindowDimensions({ width: vvW, height: vvH });
        }
        return;
      }

      // Skip while dimensions are locked (keyboard is open)
      if (lockedDimensionsRef.current) {
        return;
      }

      // Post-unlock cooldown: ignore resize events for 500ms after the
      // keyboard lock is released. Safari's keyboard-close animation fires
      // transitional viewport sizes that would briefly shrink the game.
      if (resizeCooldownRef.current > 0) {
        const elapsed = Date.now() - resizeCooldownRef.current;
        if (elapsed < 500) {
          return;
        }
        resizeCooldownRef.current = 0;
      }

      // Use visualViewport for actual visible area (excludes browser toolbars)
      const height = window.visualViewport?.height ?? window.innerHeight;
      const width = window.visualViewport?.width ?? window.innerWidth;

      setWindowDimensions((prev) => {
        if (prev.width === width && prev.height === height) {
          return prev;
        }
        return { width, height };
      });
    };

    window.addEventListener("resize", handleResize);

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleResize);
    }

    handleResize(); // Initial dimensions on mount

    return () => {
      window.removeEventListener("resize", handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleResize);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate effective dimensions, constraining width to max stage width
  // Use locked dimensions if available (keyboard open), otherwise use current window dimensions
  const dimensions = useMemo(() => {
    const source = lockedDimensions || windowDimensions;

    const effectiveWidth = Math.min(
      source.width,
      STAGE_DIMENSIONS.MAX_WIDTH,
    );
    const effectiveHeight = Math.min(
      source.height,
      STAGE_DIMENSIONS.MAX_HEIGHT,
    );

    // ── The UI in one 16:9 block (FEATURES_CONFIG.LAYOUT.NO_TABLET) ── in a
    // frame narrower than 16:9 (any desktop; a touch screen held sideways) the
    // UI is laid out for a 16:9 box of that width, centred — it does not
    // spread to the frame's edges (like 3 Pots of Destiny). Only the UI: see
    // UserInterface. Switch off = the box is the whole frame (as before).
    let boxHeight = effectiveHeight;
    let boxWidth = effectiveWidth;
    if (FEATURES_CONFIG.LAYOUT.NO_TABLET) {
      // (LAYOUT.DEVICE_BY_POINTER off: by shape only — see aspectRatioMode.)
      // const isFinePointer =
      //   FEATURES_CONFIG.LAYOUT.DEVICE_BY_POINTER &&
      //   typeof window !== "undefined" &&
      //   !window.matchMedia?.("(pointer: coarse)").matches;
      // DESKTOP by the platform's own test (isDesktopByInput, 2026-10-30) —
      // the same one aspectRatioMode picks the device with.
      const isFinePointer =
        FEATURES_CONFIG.LAYOUT.DEVICE_BY_POINTER &&
        isDesktopByInput(effectiveWidth / effectiveHeight);
      // Touch screens too, while held sideways (the same landscape test as the
      // layout's, incl. near-square) — taller and taller keeps the landscape
      // block until the frame turns portrait.
      const frameAspect = effectiveWidth / effectiveHeight;
      const isSidewaysShape =
        (Math.abs(frameAspect - 1.0) <= DEVICE_DETECTION.SQUARE_DEVICE_TOLERANCE &&
          frameAspect >= 0.85) ||
        frameAspect > DEVICE_DETECTION.LANDSCAPE_ASPECT_RATIO;
      // if (frameAspect < 16 / 9 && (isFinePointer || isSidewaysShape)) {
      // Touch: tablets only (2026-10-30) — a phone sideways under 16:9 is
      // one with Safari's bars (715x414), and the band only lifted its
      // toolbar off the bottom edge. Desktops (any frame) keep it.
      if (
        frameAspect < 16 / 9 &&
        (isFinePointer ||
          (isSidewaysShape &&
            Math.min(effectiveWidth, effectiveHeight) >=
              DEVICE_DETECTION.PHONE_MAX_SHORT_SIDE))
      ) {
        boxHeight = Math.round((effectiveWidth * 9) / 16);
      }
      // ── ...and upright, one 9:16 column (2026-10-29, the client's CEO:
      // "match their layout") ── a touch screen held upright that is wider
      // than 9:16 (every iPad) gets the PHONE's portrait UI in a 9:16 column
      // of the frame's height, centred — buttons grouped as on a phone, the
      // reel fitted to it; the background and the scene's art fill the rest.
      // if (!isFinePointer && !isSidewaysShape && frameAspect > 9 / 16) {
      // Tablets only (2026-10-30): a phone with Safari's bars is wider than
      // 9:16 too, and is a small phone (DEVICE_DETECTION.SHORT_PHONE_MIN_ASPECT).
      if (
        !isFinePointer &&
        !isSidewaysShape &&
        frameAspect > 9 / 16 &&
        Math.min(effectiveWidth, effectiveHeight) >= DEVICE_DETECTION.PHONE_MAX_SHORT_SIDE
      ) {
        boxWidth = Math.round((effectiveHeight * 9) / 16);
      }
    }

    const next = {
      width: effectiveWidth,
      height: effectiveHeight,
      windowWidth: source.width,
      windowHeight: source.height,
      // The UI's 16:9 box (UserInterface lays the HUD out in it); the game,
      // the background and the dialogs keep the full frame.
      uiBoxHeight: boxHeight,
      uiBoxOffsetY: Math.round((effectiveHeight - boxHeight) / 2),
      // The upright 9:16 column (an iPad), the frame's width otherwise.
      uiBoxWidth: boxWidth,
      uiBoxOffsetX: Math.round((effectiveWidth - boxWidth) / 2),
    };

    // Keep the SAME object reference when nothing actually changed. Locking and
    // unlocking (keyboard open/close) flips lockedDimensions between a value and
    // null while the size is identical — without this, each flip minted a new
    // `dimensions` object, rebuilt the context value, and re-rendered every
    // consumer (the popup flash on keyboard close). Values are unchanged, so
    // this only removes spurious re-renders; a real resize still produces a new
    // object because one of these fields differs.
    const prev = prevDimensionsRef.current;
    if (
      prev &&
      prev.width === next.width &&
      prev.height === next.height &&
      prev.windowWidth === next.windowWidth &&
      prev.windowHeight === next.windowHeight &&
      prev.uiBoxHeight === next.uiBoxHeight &&
      prev.uiBoxOffsetY === next.uiBoxOffsetY &&
      prev.uiBoxWidth === next.uiBoxWidth &&
      prev.uiBoxOffsetX === next.uiBoxOffsetX
    ) {
      return prev;
    }
    prevDimensionsRef.current = next;
    return next;
  }, [lockedDimensions, windowDimensions]);

  // Calculate responsive layout mode based on device type and orientation
  const aspectRatioMode = useMemo(() => {
    const { width, height } = dimensions;
    const aspectRatio = width / height;
    const minDimension = Math.min(width, height);

    let deviceType;
    let layout;
    let orientation;

    const noTablet = FEATURES_CONFIG.LAYOUT.NO_TABLET;

    if (noTablet) {
      // ── By the DEVICE, not the frame size (FEATURES_CONFIG.LAYOUT.NO_TABLET) ──
      // an embedded 16:9 frame goes from ~1200x675 down to ~360x203, so width
      // >= 1367 rarely held and its height drops under the 540 phone line in
      // ordinary laptop windows — the size rules gave them the tablet or
      // mobile layout. Their games' rule: a mouse / trackpad (fine pointer) is
      // DESKTOP whatever the frame, it only scales; a touch screen is MOBILE
      // (landscape / portrait by its shape). No tablet step.
      // const isTouchScreen =
      //   typeof window !== "undefined" &&
      //   !!window.matchMedia?.("(pointer: coarse)").matches;
      if (FEATURES_CONFIG.LAYOUT.DEVICE_BY_POINTER) {
        // deviceType = isTouchScreen
        //   ? PixiSettings.DEVICE.MOBILE
        //   : PixiSettings.DEVICE.DESKTOP;
        // The platform's own test (isDesktopByInput, 2026-10-30).
        deviceType = isDesktopByInput(aspectRatio)
          ? PixiSettings.DEVICE.DESKTOP
          : PixiSettings.DEVICE.MOBILE;
      } else {
        // By window size (LAYOUT.DEVICE_BY_POINTER off — DevTools' responsive
        // sizes keep a fine pointer): a phone-sized short side is MOBILE, a
        // frame wider than tall DESKTOP, a taller one portrait. No tablet.
        const sideways =
          (Math.abs(aspectRatio - 1.0) <= DEVICE_DETECTION.SQUARE_DEVICE_TOLERANCE &&
            aspectRatio >= 0.85) ||
          aspectRatio > DEVICE_DETECTION.LANDSCAPE_ASPECT_RATIO;
        deviceType =
          minDimension > ASPECT_RATIO_BREAKPOINTS.PHONE && sideways
            ? PixiSettings.DEVICE.DESKTOP
            : PixiSettings.DEVICE.MOBILE;
      }
    } else {
    // Device detection based on width
    // Desktop requires width >= 1367px (simplified - no aspect ratio requirement)
    if (width >= ASPECT_RATIO_BREAKPOINTS.DESKTOP_MIN_WIDTH) {
      deviceType = PixiSettings.DEVICE.DESKTOP;
    } else if (minDimension <= ASPECT_RATIO_BREAKPOINTS.PHONE) {
      deviceType = PixiSettings.DEVICE.MOBILE;
    } else {
      deviceType = PixiSettings.DEVICE.TABLET;
    }
    }

    // Promote wide mobiles to tablets
    if (!noTablet && deviceType === PixiSettings.DEVICE.MOBILE) {
      const isSurfaceDuo =
        width <= DEVICE_DETECTION.LARGE_MOBILE_WIDTH && // Only real mobile widths, not desktop browser windows
        aspectRatio >= DEVICE_DETECTION.SURFACE_DUO_ASPECT_RATIO &&
        minDimension >= DEVICE_DETECTION.SURFACE_DUO_MIN_DIMENSION;
      const isLargeMobile =
        width >= DEVICE_DETECTION.LARGE_MOBILE_WIDTH &&
        minDimension >=
          DEVICE_DETECTION.LARGE_MOBILE_MIN_DIMENSION;

      if (isSurfaceDuo || isLargeMobile) {
        deviceType = PixiSettings.DEVICE.TABLET;
      }
    }

    // Handle near-square devices
    const isSquareDevice =
      Math.abs(aspectRatio - 1.0) <=
      DEVICE_DETECTION.SQUARE_DEVICE_TOLERANCE;

    // Promote mobile square devices to tablet
    if (!noTablet && isSquareDevice && deviceType === PixiSettings.DEVICE.MOBILE) {
      deviceType = PixiSettings.DEVICE.TABLET;
    }

    // Sub-classify remaining MOBILE as MOBILE_SMALL for compact phones
    // Based on max dimension (the longer side regardless of orientation)
    if (deviceType === PixiSettings.DEVICE.MOBILE) {
      const maxDimension = Math.max(width, height);
      // if (maxDimension <= DEVICE_DETECTION.SMALL_MOBILE_MAX_DIMENSION) {
      // ...or a phone upright that is short for its width (Safari's bars).
      const isShortPhoneUpright =
        minDimension < DEVICE_DETECTION.PHONE_MAX_SHORT_SIDE &&
        aspectRatio >= DEVICE_DETECTION.SHORT_PHONE_MIN_ASPECT &&
        aspectRatio < 0.85;
      if (
        maxDimension <= DEVICE_DETECTION.SMALL_MOBILE_MAX_DIMENSION ||
        isShortPhoneUpright
      ) {
        deviceType = PixiSettings.DEVICE.MOBILE_SMALL;
      }
    }

    // Orientation
    let isLandscape;
    // Only force landscape for near-square devices (generous tolerance)
    if (isSquareDevice && aspectRatio >= 0.85) {
      // Near-square devices (aspect ratio 0.8+) use landscape layout
      // This allows up to ~100-150px height difference depending on screen size
      isLandscape = true;
    } else {
      isLandscape =
        aspectRatio > DEVICE_DETECTION.LANDSCAPE_ASPECT_RATIO;
    }

    if (deviceType === PixiSettings.DEVICE.DESKTOP) {
      orientation = PixiSettings.ORIENTATION.LANDSCAPE;
      if (width >= ULTRA_WIDE.BREAKPOINT) {
        layout = LAYOUT_TYPES.ULTRA_WIDE_DESKTOP;
      } else if (width >= ASPECT_RATIO_BREAKPOINTS.WIDE_DESKTOP_MIN_WIDTH) {
        layout = LAYOUT_TYPES.WIDE_DESKTOP;
      } else if (width <= ASPECT_RATIO_BREAKPOINTS.SMALL_DESKTOP_MAX_WIDTH) {
        layout = LAYOUT_TYPES.SMALL_DESKTOP;
      } else {
        layout = LAYOUT_TYPES.DESKTOP;
      }
    } else {
      orientation = isLandscape
        ? PixiSettings.ORIENTATION.LANDSCAPE
        : PixiSettings.ORIENTATION.PORTRAIT;

      if (deviceType === PixiSettings.DEVICE.MOBILE_SMALL) {
        layout = isLandscape
          ? LAYOUT_TYPES.MOBILE_SMALL_LANDSCAPE
          : LAYOUT_TYPES.MOBILE_SMALL_PORTRAIT;
      } else if (deviceType === PixiSettings.DEVICE.MOBILE) {
        // layout = isLandscape
        //   ? LAYOUT_TYPES.MOBILE_LANDSCAPE
        //   : LAYOUT_TYPES.MOBILE_PORTRAIT;
        // ── An iPad upright (2026-10-29, the client's CEO: "match their
        // layout") ── a portrait frame wider than 9:16 is the MOBILE device
        // (its UI, buttons, places) on the TABLET_PORTRAIT layout: that
        // layout's own paddings and per-layout values. A phone (taller than
        // 9:16) stays MOBILE_PORTRAIT, untouched.
        // ...and held SIDEWAYS narrower than 16:9 (an iPad's 4:3): the
        // MOBILE device on the TABLET_LANDSCAPE layout. A phone (16:9 or
        // wider) stays MOBILE_LANDSCAPE, untouched.
        layout = isLandscape
          ? noTablet && aspectRatio < 16 / 9
            ? LAYOUT_TYPES.TABLET_LANDSCAPE
            : LAYOUT_TYPES.MOBILE_LANDSCAPE
          : noTablet && aspectRatio > 9 / 16
            ? LAYOUT_TYPES.TABLET_PORTRAIT
            : LAYOUT_TYPES.MOBILE_PORTRAIT;
      } else {
        layout = isLandscape
          ? LAYOUT_TYPES.TABLET_LANDSCAPE
          : LAYOUT_TYPES.TABLET_PORTRAIT;
      }
    }

    return {
      config: LAYOUT_CONFIGS[layout],
      device: deviceType,
      orientation,
      layout,
    };
  }, [dimensions]);

  // Reset iOS fullscreen state when orientation/device changes to portrait or desktop
  useEffect(() => {
    // Only reset if we're on iOS Safari
    if (!isIOS() || !isSafari()) {
      return;
    }

    // Reset if:
    // 1. Orientation is portrait (not landscape)
    // 2. Device is desktop
    const shouldReset =
      aspectRatioMode.orientation === PixiSettings.ORIENTATION.PORTRAIT ||
      aspectRatioMode.device === PixiSettings.DEVICE.DESKTOP;

    if (shouldReset) {
      pauseIosFullscreenState();
    }
  }, [aspectRatioMode.orientation, aspectRatioMode.device]);

  // Calculate symbol dimensions and scaling for the game reel area
  const symbolDimensions = useMemo(() => {
    // Use reelset-specific layout configs with reduced padding for bigger symbols
    let config = PixiSettings.REELSET_LAYOUT_CONFIGS[aspectRatioMode.layout];
    if (!config) {
      // Fallback to minimal padding
      config = { paddingSides: 0, paddingTop: 0, paddingBottom: 0 };
    }
    // const numDividers = Settings.REEL_COUNT - 1;
    const totalDividerWidth = FEATURES_CONFIG.DIVIDERS.ENABLED
      ? (Settings.REEL_COUNT - 1) * DIVIDER_MAX_WIDTH
      : 0;

    const nativeGameWidth =
      Settings.REEL_COUNT * Settings.SYMBOL_WIDTH + totalDividerWidth;
    const nativeGameHeight = Settings.SYMBOLS_VISIBLE * Settings.SYMBOL_HEIGHT;

    const paddingWidth = config.paddingSides;
    const paddingHeight = config.paddingTop + config.paddingBottom;
    const totalNeededWidth = nativeGameWidth + paddingWidth;
    const totalNeededHeight = nativeGameHeight + paddingHeight;

    // Fitted into the UI's 16:9 box: in a frame narrower than 16:9 the reel
    // and the scene keep inside the HUD block, exactly as in a 16:9 frame of
    // that width (both are centred on the frame). Without the switch the box
    // is the whole frame.
    let scaleFactor = Math.min(
      dimensions.width / totalNeededWidth,
      (dimensions.uiBoxHeight ?? dimensions.height) / totalNeededHeight,
    );

    // Cap at 1.0 normally; allow scaling up on ultra-wide monitors
    const maxScale = dimensions.width > ULTRA_WIDE.BREAKPOINT ? ULTRA_WIDE.MAX_SCALE : 1;
    scaleFactor = Math.min(maxScale, scaleFactor);

    const symbolWidth = Math.round(Settings.SYMBOL_WIDTH * scaleFactor);
    const symbolHeight = Math.round(Settings.SYMBOL_HEIGHT * scaleFactor);

    return {
      symbolWidth,
      symbolHeight,
      scaleFactor,
      totalNeededWidth,
      totalNeededHeight,
      paddingWidth,
      paddingHeight,
    };
  }, [dimensions.width, dimensions.height, dimensions.uiBoxHeight, aspectRatioMode]);

  const dividerDimensions = useMemo(() => {
    if (!FEATURES_CONFIG.DIVIDERS.ENABLED) {
      return { width: 0, height: 0 };
    }
    const scaledWidth =
      (DIVIDER_MAX_WIDTH * symbolDimensions.symbolWidth) /
      Settings.SYMBOL_WIDTH;
    const width = Math.min(
      DIVIDER_MAX_WIDTH,
      Math.round(scaledWidth)
    );
    const height = symbolDimensions.symbolHeight * Settings.SYMBOLS_VISIBLE;
    return { width, height };
  }, [symbolDimensions.symbolWidth, symbolDimensions.symbolHeight]);

  // UI-specific dimensions — fixed reference grid (5 reels × 200px + 4 dividers × 8px)
  // These values NEVER change regardless of actual reel count or symbol size
  const uiDimensions = useMemo(() => {
    const config = aspectRatioMode.config;

    const nativeGameWidth = 1032;  // 5 × 200 + 4 × 8
    const nativeGameHeight = 600;  // 3 × 200

    const paddingWidth = config.paddingSides;
    const paddingHeight = config.paddingTop + config.paddingBottom;
    const totalNeededWidth = nativeGameWidth + paddingWidth;
    const totalNeededHeight = nativeGameHeight + paddingHeight;

    // let scaleFactor = Math.min(
    //   dimensions.width / totalNeededWidth,
    //   dimensions.height / totalNeededHeight,
    // );
    // The width of the upright 9:16 column on an iPad (uiBoxWidth): the
    // phone's UI there, not one sized off the frame. The frame's width
    // everywhere else.
    let scaleFactor = Math.min(
      (dimensions.uiBoxWidth ?? dimensions.width) / totalNeededWidth,
      dimensions.height / totalNeededHeight,
    );

    // UI scale boost for wider screens (bigger buttons/text)
    const UI_SCALE_CAPS = {
      ULTRA_WIDE: 1.15,    // 15% bigger on ultra-wide
      WIDE: 1.1,           // 10% bigger on wide desktop
      DEFAULT: 1.15,        // 15% bigger on standard desktop
    };

    let maxUIScale;
    if (dimensions.width >= ULTRA_WIDE.BREAKPOINT) {
      maxUIScale = UI_SCALE_CAPS.ULTRA_WIDE;
    } else if (dimensions.width >= ASPECT_RATIO_BREAKPOINTS.WIDE_DESKTOP_MIN_WIDTH) {
      maxUIScale = UI_SCALE_CAPS.WIDE;
    } else {
      maxUIScale = UI_SCALE_CAPS.DEFAULT;
    }
    scaleFactor = Math.min(maxUIScale, scaleFactor);

    const symbolWidth = Math.round(GRID_SYMBOL_WIDTH * scaleFactor);
    const symbolHeight = Math.round(GRID_SYMBOL_HEIGHT * scaleFactor);

    return {
      symbolWidth,
      symbolHeight,
      scaleFactor,
      totalNeededWidth,
      totalNeededHeight,
      paddingWidth,
      paddingHeight,
    };
  }, [dimensions.width, dimensions.height, dimensions.uiBoxWidth, aspectRatioMode]);

  // Responsive configuration helper
  const getResponsiveConfig = useMemo(() => {
    return (config, useUIDimensions = false) => {
      // Choose which dimensions to use
      const dims = useUIDimensions ? uiDimensions : symbolDimensions;
      const baseHeight = useUIDimensions
        ? GRID_SYMBOL_HEIGHT
        : Settings.SYMBOL_HEIGHT;

      let deviceConfig;
      if (aspectRatioMode.device === PixiSettings.DEVICE.DESKTOP) {
        deviceConfig = config.DEVICE_CONFIGS[aspectRatioMode.device];
      } else {
        deviceConfig =
          config.DEVICE_CONFIGS[aspectRatioMode.device]?.[
            aspectRatioMode.orientation
          ]; // Desktop uses landscape orientation by default

        // Fallback: MOBILE_SMALL → MOBILE if not explicitly defined in config
        if (
          !deviceConfig &&
          aspectRatioMode.device === PixiSettings.DEVICE.MOBILE_SMALL
        ) {
          deviceConfig =
            config.DEVICE_CONFIGS[PixiSettings.DEVICE.MOBILE]?.[
              aspectRatioMode.orientation
            ];
        }
      }

      if (!deviceConfig) {
        return {};
      }

      // Merge with defaults if they exist
      if (config.DEFAULTS) {
        deviceConfig = { ...config.DEFAULTS, ...deviceConfig };
      }

      // A LAYOUT's own row on top (2026-10-29) — e.g. TABLET_PORTRAIT, an
      // iPad upright on the MOBILE device: only a config that has one.
      const layoutRow = config.LAYOUT_CONFIGS?.[aspectRatioMode.layout];
      if (layoutRow) {
        deviceConfig = { ...deviceConfig, ...layoutRow };
      }

      let scaledWidth, scaledHeight;
      const symbolScale = dims.symbolHeight / baseHeight;

      if (config.BASE_PORTRAIT_WIDTH && config.BASE_LANDSCAPE_WIDTH) {
        const shouldUsePortrait =
          (aspectRatioMode.orientation === PixiSettings.ORIENTATION.PORTRAIT &&
            isMobileDevice(aspectRatioMode.device)) ||
          aspectRatioMode.device === PixiSettings.DEVICE.TABLET;

        const baseWidth = shouldUsePortrait
          ? config.BASE_PORTRAIT_WIDTH
          : config.BASE_LANDSCAPE_WIDTH;
        const baseHeightValue = shouldUsePortrait
          ? config.BASE_PORTRAIT_HEIGHT
          : config.BASE_LANDSCAPE_HEIGHT;

        scaledWidth = Math.round(
          baseWidth * deviceConfig.deviceMultiplier * symbolScale,
        );
        scaledHeight = Math.round(
          baseHeightValue * deviceConfig.deviceMultiplier * symbolScale,
        );
      } else {
        scaledWidth = Math.round(
          config.BASE_WIDTH * deviceConfig.deviceMultiplier * symbolScale,
        );
        scaledHeight = Math.round(
          config.BASE_HEIGHT * deviceConfig.deviceMultiplier * symbolScale,
        );
      }

      let fontSize;
      if (config.FONT_SIZE_RATIO) {
        fontSize = Math.floor(scaledHeight * config.FONT_SIZE_RATIO);
      }

      // Shrink buttons slightly on mobile portrait for better proportions
      const isPortraitMobile =
        isMobileDevice(aspectRatioMode.device) &&
        aspectRatioMode.orientation === PixiSettings.ORIENTATION.PORTRAIT;
      const portraitShrink = isPortraitMobile ? 0.9 : 1.0;

      return {
        width: Math.round(scaledWidth * portraitShrink),
        height: Math.round(scaledHeight * portraitShrink),
        fontSize,
        ...deviceConfig,
      };
    };
  }, [
    aspectRatioMode.device,
    aspectRatioMode.orientation,
    aspectRatioMode.layout,
    symbolDimensions,
    uiDimensions,
  ]);

  // Memoize derived values to prevent recalculation
  const gameScale = useMemo(
    () => symbolDimensions.symbolHeight / Settings.SYMBOL_HEIGHT,
    [symbolDimensions.symbolHeight],
  );
  const uiScale = useMemo(
    () => uiDimensions.symbolHeight / GRID_SYMBOL_HEIGHT,
    [uiDimensions.symbolHeight],
  );
  const reelSpacing = useMemo(
    () => symbolDimensions.symbolWidth + dividerDimensions.width,
    [symbolDimensions.symbolWidth, dividerDimensions.width],
  );
  const isMobilePortrait = useMemo(
    () =>
      isMobileDevice(aspectRatioMode.device) &&
      aspectRatioMode.orientation === PixiSettings.ORIENTATION.PORTRAIT,
    [aspectRatioMode.device, aspectRatioMode.orientation],
  );
  const isMobileSmall = useMemo(
    () => aspectRatioMode.device === PixiSettings.DEVICE.MOBILE_SMALL,
    [aspectRatioMode.device],
  );
  const isKeyboardOpen = lockedDimensions !== null;

  // Backward-compatible device: MOBILE_SMALL → MOBILE for all existing
  // position configs and direct device checks. Only getResponsiveConfig
  // uses the real device internally (with MOBILE_SMALL → MOBILE fallback).
  const compatDevice = useMemo(
    () =>
      aspectRatioMode.device === PixiSettings.DEVICE.MOBILE_SMALL
        ? PixiSettings.DEVICE.MOBILE
        : aspectRatioMode.device,
    [aspectRatioMode.device],
  );

  // Consolidated context value
  const value = useMemo(() => {
    return {
      dimensions,

      // Game dimensions (adapts to actual SYMBOL_WIDTH/HEIGHT)
      symbolWidth: symbolDimensions.symbolWidth,
      symbolHeight: symbolDimensions.symbolHeight,
      dividerDimensions,
      reelSpacing,
      // symbolHeight / SYMBOL_HEIGHT — matches the ROUNDED pixel sizes above,
      // for anything laid out in pixels.
      symbolScale: gameScale,
      // The same scale unrounded — continuous while the window is dragged.
      // For things that are scaled as a whole (the base game view spine and
      // its native board), where the rounded one steps by ~0.5%.
      symbolScaleExact: symbolDimensions.scaleFactor,

      // UI dimensions (static based on GRID_SYMBOL_WIDTH/HEIGHT)
      uiSymbolWidth: uiDimensions.symbolWidth,
      uiSymbolSize: uiDimensions.symbolWidth,
      uiSymbolHeight: uiDimensions.symbolHeight,
      uiSymbolScale: uiScale,

      device: compatDevice,
      orientation: aspectRatioMode.orientation,
      // The resolved layout key — one of LAYOUT_TYPES. Finer-grained than
      // device + orientation, because it also splits desktop into small /
      // standard / wide / ultra-wide, which device alone cannot express.
      layout: aspectRatioMode.layout,
      isMobilePortrait,
      isMobileSmall,
      isKeyboardOpen,

      getResponsiveConfig,
    };
  }, [
    dimensions,
    symbolDimensions.symbolWidth,
    symbolDimensions.symbolHeight,
    symbolDimensions.scaleFactor,
    dividerDimensions,
    reelSpacing,
    gameScale,
    uiDimensions.symbolWidth,
    uiDimensions.symbolHeight,
    uiScale,
    compatDevice,
    aspectRatioMode.orientation,
    aspectRatioMode.layout,
    isMobilePortrait,
    isMobileSmall,
    isKeyboardOpen,
    getResponsiveConfig,
  ]);

  // The first value is there for the first render of every follower.
  if (!dimensionsStore.value) dimensionsStore.value = value;
  // Published after the commit, then every follower is told; a hidden one
  // reads its frozen copy and does not re-render.
  useLayoutEffect(() => {
    if (dimensionsStore.value === value) return;
    dimensionsStore.value = value;
    for (const listener of dimensionsStore.listeners) listener();
  }, [value]);

  return (
    <DimensionsContext.Provider value={value}>
      {children}
    </DimensionsContext.Provider>
  );
};

/**
 * The dimensions, followed only while `active`.
 *
 * Same value as useDimensions() while `active` is true. While it is false the
 * component keeps the value it last had and a resize does not re-render it —
 * for things that stay mounted (warm) but are not on screen: hidden dialogs,
 * closed popups, an inactive room. When `active` turns true, the render that
 * shows it already has the current size, so it lays out before it is seen.
 */
const subscribeDimensions = (onChange) => {
  dimensionsStore.listeners.add(onChange);
  return () => dimensionsStore.listeners.delete(onChange);
};

export const useDimensionsWhen = (active) => {
  if (!dimensionsStore.value) {
    throw new Error("useDimensionsWhen must be used within a DimensionsProvider");
  }
  const frozenRef = useRef(null);
  const getSnapshot = () => {
    if (active || !frozenRef.current) frozenRef.current = dimensionsStore.value;
    return frozenRef.current;
  };
  return useSyncExternalStore(subscribeDimensions, getSnapshot);
};

/**
 * The HUD's view of a dimensions value (FEATURES_CONFIG.LAYOUT.NO_TABLET):
 * its height is the UI's 16:9 box (its width the upright 9:16 column on an
 * iPad, 2026-10-29). UserInterface hands it to the HUD as its
 * context; a HUD popup that reads useDimensionsWhen (the store, not that
 * context) passes its value through here to see the same box. The same object
 * back when there is no box.
 */
export const hudDimensions = (value) => {
  const { dimensions } = value;
  // if (!dimensions.uiBoxHeight || dimensions.uiBoxHeight === dimensions.height) return value;
  // return { ...value, dimensions: { ...dimensions, height: dimensions.uiBoxHeight } };
  // ...or the upright 9:16 column's width (an iPad, 2026-10-29).
  const boxedHeight = !!dimensions.uiBoxHeight && dimensions.uiBoxHeight !== dimensions.height;
  const boxedWidth = !!dimensions.uiBoxWidth && dimensions.uiBoxWidth !== dimensions.width;
  if (!boxedHeight && !boxedWidth) return value;
  return {
    ...value,
    dimensions: {
      ...dimensions,
      height: boxedHeight ? dimensions.uiBoxHeight : dimensions.height,
      width: boxedWidth ? dimensions.uiBoxWidth : dimensions.width,
    },
    // In the column the HUD takes the SMALL phone's places (2026-10-29: their
    // buttons sit higher, as our mobile-small ones do). Places only: sizes
    // come from getResponsiveConfig, which reads the real device.
    ...(boxedWidth ? { isMobileSmall: true } : null),
  };
};

// Hook for consuming the DimensionsContext
export const useDimensions = () => {
  const context = useContext(DimensionsContext);
  if (!context) {
    throw new Error("useDimensions must be used within a DimensionsProvider");
  }
  return context;
};
